import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/** Milliseconds between polls of the fallback settle detector. */
const SETTLE_POLL_INTERVAL = 60;

/** Maximum number of polls before the fallback settle detector gives up. */
const SETTLE_POLL_LIMIT = 25;

/** Safety net for browsers that expose `scrollend` but never fire it. */
const SETTLE_TIMEOUT = 1000;

/** Sub-pixel tolerance when comparing against the two ends of the track. */
const EDGE_TOLERANCE = 1;

/**
 * Upsell / cross-sell carousel.
 *
 * Progressive enhancement only. The track is a native scroll container with
 * `scroll-snap-type: x mandatory` (the same mechanism `slideshow-slides` uses
 * in this theme), so with JavaScript disabled every card is still reachable by
 * swiping, by the keyboard once the track is focused, and by a screen reader.
 * This class only adds the arrows, the progress bar and the announcement.
 *
 * A step is one full viewport of the track, not `n * card width`. Because card
 * width is derived from the `--upsell-per-view` custom property, one viewport
 * is always exactly one page at every breakpoint, and `scroll-snap` absorbs the
 * gap rounding afterwards. That keeps all breakpoint knowledge in CSS and means
 * this file has no media queries of its own.
 *
 * The arrows are wired with the base `Component` class's declarative
 * `on:click` bindings, dispatched by its single document-level delegated
 * listener, so the only listeners this class registers itself are the ones the
 * delegation does not cover: `scroll` (which does not bubble) and `scrollend`.
 *
 * @typedef {object} ProductUpsellRefs
 * @property {HTMLElement} [track] - The horizontally scrolling container.
 * @property {HTMLElement} [list] - The flex list inside the track.
 * @property {HTMLElement[]} [items] - The card list items, in DOM order.
 * @property {HTMLElement} [controls] - The wrapper around the arrow pair.
 * @property {HTMLButtonElement} [previous] - The previous arrow.
 * @property {HTMLButtonElement} [next] - The next arrow.
 * @property {HTMLElement} [progress] - The progress rail.
 * @property {HTMLElement} [progressBar] - The progress thumb.
 * @property {HTMLElement} [status] - The visually hidden live region.
 *
 * @extends {Component<ProductUpsellRefs>}
 */
export class ProductUpsellComponent extends Component {
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {number | undefined} */
  #settleTimeout;

  /** @type {number | undefined} */
  #settlePoll;

  /** @type {(() => void) | undefined} */
  #cancelSettle;

  /** @type {boolean} */
  #scrollTicking = false;

  connectedCallback() {
    super.connectedCallback();

    const { track } = this.refs;
    if (!track) return;

    track.addEventListener('scroll', this.#handleScroll, { passive: true });

    // The track's own width changes with the viewport, but a breakpoint change
    // also rewrites `--upsell-per-view`, which resizes the cards while leaving
    // the track's width alone. Observing a card as well as the track catches
    // both, so no `resize` listener and no JS breakpoint logic are needed.
    this.#resizeObserver = new ResizeObserver(() => this.#update());
    this.#resizeObserver.observe(track);

    const firstItem = this.refs.items?.[0];
    if (firstItem) this.#resizeObserver.observe(firstItem);

    this.#update();
  }

  /**
   * The Section Rendering API can morph cards into or out of the track without
   * anything changing size, which is the one way the row's geometry changes
   * that neither the scroll listener nor the ResizeObserver would notice.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.track?.removeEventListener('scroll', this.#handleScroll);

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;

    this.#cancelSettle?.();
  }

  /**
   * Advances the track by one page.
   *
   * @param {Event} event - The click event from the `on:click="/next"` binding.
   */
  next(event) {
    this.#step(1, this.#buttonFrom(event));
  }

  /**
   * Rewinds the track by one page.
   *
   * @param {Event} event - The click event from the `on:click="/previous"` binding.
   */
  previous(event) {
    this.#step(-1, this.#buttonFrom(event));
  }

  /**
   * Resolves the button that was activated. The base class proxies
   * `event.target` to the element carrying the `on:click` attribute, so this
   * is the arrow itself even when the press landed on its SVG.
   *
   * @param {Event} event
   * @returns {HTMLButtonElement | null}
   */
  #buttonFrom(event) {
    const { target } = event;
    return target instanceof HTMLButtonElement ? target : null;
  }

  /**
   * Scrolls one page in `direction`, then reconciles the controls once the
   * scroll has actually finished.
   *
   * @param {number} direction - 1 to advance, -1 to rewind.
   * @param {HTMLButtonElement | null} button - The arrow that was activated.
   */
  #step(direction, button) {
    const { track } = this.refs;
    if (!track) return;

    track.scrollBy({
      left: direction * track.clientWidth,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });

    this.#whenSettled(() => {
      this.#update();

      // Announce only on explicit activation. Announcing from the scroll
      // handler would flood the screen reader during a swipe.
      this.#announce();

      // APG: when the arrow the user just pressed becomes disabled, focus must
      // not be dropped on the body. Hand it to its counterpart.
      if (button?.disabled) {
        const { previous, next } = this.refs;
        const counterpart = button === next ? previous : next;
        if (counterpart && !counterpart.disabled) counterpart.focus();
      }
    });
  }

  /**
   * Reconciles everything that depends on scroll position or track geometry:
   * whether the controls are relevant at all, the disabled state of each arrow,
   * and the size and offset of the progress thumb.
   *
   * The controls and the progress bar are rendered server-side whenever there
   * are more products than fit one desktop page, then hidden here whenever the
   * track does not actually overflow at the current breakpoint. That covers the
   * cases Liquid cannot know about, such as a narrow window at 3-up.
   */
  #update() {
    const { track, controls, progress, progressBar, previous, next } = this.refs;
    if (!track) return;

    const max = track.scrollWidth - track.clientWidth;
    const overflows = max > EDGE_TOLERANCE;

    if (controls) controls.hidden = !overflows;
    if (progress) progress.hidden = !overflows;
    if (!overflows) return;

    const position = track.scrollLeft;

    if (previous) previous.disabled = position <= EDGE_TOLERANCE;
    if (next) next.disabled = position >= max - EDGE_TOLERANCE;

    if (!progressBar) return;

    // Thumb size is the share of the track that is visible; its offset is that
    // same share of the rail that remains.
    const visibleRatio = track.clientWidth / track.scrollWidth;
    const scrolledRatio = max > 0 ? position / max : 0;

    progressBar.style.inlineSize = `${visibleRatio * 100}%`;
    progressBar.style.marginInlineStart = `${scrolledRatio * (100 - visibleRatio * 100)}%`;
  }

  /**
   * Writes the currently visible range into the live region.
   *
   * The range is derived from the settled scroll position rather than from a
   * running counter, so it stays correct after a swipe, a keyboard scroll, or
   * a snap correction that moved further than the arrow asked for.
   */
  #announce() {
    const { track, list, items, status } = this.refs;
    if (!track || !status || !items?.length) return;

    const cardWidth = items[0]?.getBoundingClientRect().width ?? 0;
    if (!cardWidth) return;

    const gap = list ? parseFloat(getComputedStyle(list).columnGap) || 0 : 0;
    const stride = cardWidth + gap;
    const total = items.length;

    const first = Math.round(track.scrollLeft / stride) + 1;
    const perView = Math.round(track.clientWidth / stride);
    const last = Math.min(first + perView - 1, total);

    status.textContent = `Produsele ${first}–${last} din ${total}`;
  }

  /**
   * Runs `callback` once the track has stopped moving.
   *
   * A fixed `setTimeout` races the smooth scroll and the snap correction that
   * follows it, so it reads `scrollLeft` mid-flight and reports the wrong item
   * range. `scrollend` is the correct signal; the poll is the fallback for
   * browsers that do not implement it.
   *
   * Only one settle can be pending at a time. A second arrow press cancels the
   * first so the callback cannot run twice against one scroll.
   *
   * @param {() => void} callback
   */
  #whenSettled(callback) {
    const { track } = this.refs;
    if (!track) return;

    this.#cancelSettle?.();

    let done = false;

    const cleanup = () => {
      done = true;
      track.removeEventListener('scrollend', finish);
      if (this.#settleTimeout) clearTimeout(this.#settleTimeout);
      if (this.#settlePoll) clearTimeout(this.#settlePoll);
      this.#settleTimeout = undefined;
      this.#settlePoll = undefined;
      this.#cancelSettle = undefined;
    };

    function finish() {
      if (done) return;
      cleanup();
      callback();
    }

    this.#cancelSettle = () => {
      if (!done) cleanup();
    };

    if ('onscrollend' in window) {
      track.addEventListener('scrollend', finish);
      this.#settleTimeout = setTimeout(finish, SETTLE_TIMEOUT);
      return;
    }

    let previousPosition = null;
    let tries = 0;

    const poll = () => {
      if (done) return;
      if (track.scrollLeft === previousPosition || tries > SETTLE_POLL_LIMIT) {
        finish();
        return;
      }
      tries += 1;
      previousPosition = track.scrollLeft;
      this.#settlePoll = setTimeout(poll, SETTLE_POLL_INTERVAL);
    };

    poll();
  }

  /**
   * Keeps the arrows and the progress bar in sync while the visitor swipes or
   * drags, throttled to one reconcile per frame.
   */
  #handleScroll = () => {
    if (this.#scrollTicking) return;
    this.#scrollTicking = true;

    requestAnimationFrame(() => {
      this.#update();
      this.#scrollTicking = false;
    });
  };
}

if (!customElements.get('product-upsell-component')) {
  customElements.define('product-upsell-component', ProductUpsellComponent);
}
